<template>
  <v-menu
    bottom
    left
    offset-y
    :close-on-content-click="false"
    max-width="380"
    min-width="350"
  >
    <template #activator="{ on, attrs }">
      <v-btn dark icon v-bind="attrs" small v-on="on">
        <v-badge
          :content="unreadCount"
          :value="unreadCount > 0"
          overlap
          color="red"
          offset-x="10"
          offset-y="10"
        >
          <v-img
            width="25"
            height="25"
            contain
            src="@/assets/img/navbar_icons/notification-icon.svg"
          />
        </v-badge>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="pa-3">
        <span class="text-h6 font-weight-bold">Notificaciones</span>
        <v-spacer />

        <v-chip
          v-if="unreadCount > 0"
          color="red"
          text-color="white"
          small
          class="ml-2"
        >
          {{ unreadCount }}
        </v-chip>
      </v-card-title>

      <v-divider />

      <div v-if="notificationsByYear.length === 0" class="pa-3 text-center">
        <v-icon size="64" color="grey lighten-1" class="mb-3">
          mdi-bell-off-outline
        </v-icon>
        <div class="text-body-1 grey--text text--darken-1 font-weight-medium">
          No tienes notificaciones nuevas
        </div>
        <div class="text-caption grey--text mt-2">
          Las notificaciones aparecerán aquí cuando las recibas
        </div>
      </div>

      <v-list v-else max-height="400px" class="pa-0">
        <v-list-item
          v-for="(notification, i) in notificationsByYear"
          :key="`notification-${
            notification.id || notification.notification_id
          }-${i}`"
          three-line
          :class="{
            'notification-unread': !notification.read,
            'notification-loading': loadingNotificationId === notification.id,
          }"
          :ripple="loadingNotificationId !== notification.id"
          @click="openNotification(notification)"
        >
          <v-list-item-avatar>
            <v-badge
              :value="!notification.read"
              color="rgb(13, 110, 253)"
              dot
              overlap
              offset-x="10"
              offset-y="10"
            >
              <v-avatar
                :color="
                  notification.read ? 'grey lighten-2' : 'rgb(13, 110, 253)'
                "
                size="40"
              >
                <v-progress-circular
                  v-if="loadingNotificationId === notification.id"
                  indeterminate
                  color="white"
                  size="20"
                  width="2"
                />
                <v-icon
                  v-else
                  :color="notification.read ? 'grey' : 'white'"
                  size="20"
                >
                  mdi-bell-ring
                </v-icon>
              </v-avatar>
            </v-badge>
          </v-list-item-avatar>

          <v-list-item-content>
            <div
              :class="{ 'font-weight-bold': !notification.read }"
              class="text-body-2 text-left"
            >
              Se ha agregado una nueva actualización IEL
            </div>

            <v-list-item-subtitle class="mt-1 d-flex align-center">
              <div class="flex-1 justify-center">
                <v-chip
                  v-if="notification.version && notification.version !== '0'"
                  x-small
                  color="blue-grey lighten-4"
                  text-color="blue-grey darken-2"
                >
                  Versión: {{ notification.version }}
                </v-chip>
              </div>

              <span class="text-caption grey--text">
                {{
                  formatDate(
                    notification.published_at || notification.timestamp
                  )
                }}
              </span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
  import moment from 'moment';
  import { mapGetters, mapActions } from 'vuex';

  export default {
    name: 'Notifications',

    data() {
      return {
        loadingNotificationId: null,
      };
    },

    computed: {
      ...mapGetters('userNotification', [
        'notificationsByYear',
        'unreadCount',
        'notifications',
        'selectedNotificationDetails',
      ]),
    },

    mounted() {
      const userId = this.$store.getters['auth/user']?.id;
      if (userId) {
        this.$store.dispatch('userNotification/fetchNotificationsByRole');
      }
    },

    methods: {
      ...mapActions('userNotification', [
        'checkUserNotification',
        'selectNotification',
      ]),

      ...mapActions('modalStepsAfterLogin', [
        'updateModalState',
        'findStepByKey',
        'changeCurrentStepValue',
        'updateStepStatus',
        'loadNotificationPreviewStep',
      ]),

      async openNotification(notification) {
        if (this.loadingNotificationId === notification.id) return;

        this.loadingNotificationId = notification.id;

        try {
          const notificationId =
            notification.id || notification.notification_id;

          await this.selectNotification(notificationId);
          await this.checkUserNotification();
          await this.loadNotificationPreviewStep();

          const openPreviewStep = await this.findStepByKey(
            'notificationPreview'
          );

          if (openPreviewStep) {
            openPreviewStep.visited = true;
            this.updateStepStatus(openPreviewStep);
            this.changeCurrentStepValue(openPreviewStep.value);
            this.updateModalState(true);

            this.$emit('notificationOpened', notification);
          } else {
            this.$toast.warning(
              'No se pudo cargar el contenido de la notificación'
            );
          }
        } catch (error) {
          this.$toast.error('Error al procesar la notificación');
        } finally {
          this.loadingNotificationId = null;
        }
      },

      formatDate(dateString) {
        if (!dateString) return 'Fecha no disponible';

        const date = moment(dateString);
        if (!date.isValid()) return 'Fecha no disponible';

        const now = moment();
        const diffDays = now.diff(date, 'days');

        if (diffDays < 7) {
          return date.fromNow();
        } else {
          return date.format('D MMM');
        }
      },
    },
  };
</script>

<style scoped>
  .notification-unread {
    background-color: #e3f2fd !important;
    border-left: 3px solid rgb(13, 110, 253);
  }

  .notification-loading {
    opacity: 0.6;
    cursor: wait !important;
    pointer-events: none;
  }

  /*The background color was added because they were transparent
 and the content was not visible in the previous views.*/
  .v-list-item {
    background-color: white !important;
  }

  .v-list-item:not(.notification-loading):hover {
    background-color: #f5f5f5 !important;
  }

  .notification-unread:not(.notification-loading):hover {
    background-color: #d1e8ff !important;
  }

  .v-list-item {
    transition: background-color 0.2s ease, border-left-color 0.2s ease;
  }
</style>
